import { hash, load, save } from './store.js';

// Whether ads and statistics may run for this visitor. A site can simply tell visitors about them (the default), or ask
// first (Design > Notice to visitors), in which case nothing starts until the visitor accepts. The choice is kept in the
// visitor's own browser, and asked again if the notice's wording changes.
export function createConsent(design) {
  // Read each time, so the preview in the dashboard follows settings that are being changed.
  const asks = () => Boolean(design['notice.enabled'] && design['notice.mode'] === 'consent' && design['notice.text']);
  const stamp = () => hash(design['notice.text'] ?? '');
  const listeners = [];

  const raw = () => {
    const saved = load('consent', null);
    if (saved && typeof saved === 'object' && saved.stamp === stamp()) {
      return saved;
    }
    return null;
  };

  const choice = () => {
    const r = raw();
    if (!r) return null;
    if (r.choice === 'accepted' || r.choice === 'declined' || r.choice === 'custom') {
      return r.choice;
    }
    return null;
  };

  const preferences = () => {
    const r = raw();
    if (!r) return { essential: true, analytics: true, ads: true };
    if (r.choice === 'declined') return { essential: true, analytics: false, ads: false };
    if (r.choice === 'accepted') return { essential: true, analytics: true, ads: true };
    return {
      essential: true,
      analytics: Boolean(r.analytics),
      ads: Boolean(r.ads),
    };
  };

  const ok = (type) => {
    if (!asks()) return true;
    const ch = choice();
    if (!ch) return false;
    if (ch === 'accepted') return true;
    if (ch === 'declined') return false;
    const prefs = preferences();
    if (type === 'analytics') return prefs.analytics;
    if (type === 'ads' || type === 'marketing') return prefs.ads;
    return prefs.analytics || prefs.ads;
  };

  return {
    get asks() {
      return asks();
    },
    ok,
    /** 'accepted', 'declined', 'custom' or null until the visitor has chosen */
    choice,
    preferences,
    set(answer, cats = {}) {
      const was = ok();
      const payload = {
        choice: answer,
        essential: true,
        analytics: answer === 'accepted' ? true : answer === 'declined' ? false : Boolean(cats.analytics),
        ads: answer === 'accepted' ? true : answer === 'declined' ? false : Boolean(cats.ads),
        stamp: stamp(),
      };
      save('consent', payload);
      if (ok() && !was) listeners.forEach((fn) => fn());
      // Scripts that have already started cannot be taken back, so withdrawing starts the page again without them.
      else if (!ok() && was) location.reload();
    },
    onAccept: (fn) => listeners.push(fn),
  };
}
